import type {
  PayorCappedRentalsForm,
  PayorFeeScheduleRow,
  PayorOwnershipTransferForm,
  PayorPolicyRulesForm,
  PayorPriorAuthForm,
  PayorStatus,
  Plan,
} from '../../data/payorData'

export type {
  PayorCappedRentalsForm,
  PayorFeeScheduleRow,
  PayorOwnershipTransferForm,
  PayorPolicyRulesForm,
  PayorPriorAuthForm,
}

export type PayorConfigTabId =
  | 'overview'
  | 'plans'
  | 'fee-schedules'
  | 'capped-rentals'
  | 'ownership-transfer'
  | 'prior-auth'

export const PAYOR_CONFIG_TABS: { id: PayorConfigTabId; label: string }[] = [
  { id: 'overview', label: 'Payor Details' },
  { id: 'plans', label: 'Plans & Policies' },
  { id: 'fee-schedules', label: 'Fee schedules' },
  { id: 'capped-rentals', label: 'Capped rentals' },
  { id: 'ownership-transfer', label: 'Ownership transfer' },
  { id: 'prior-auth', label: 'Prior auth' },
]

export interface PayorOverviewForm {
  payorName: string
  npiTaxId: string
  contractType: string
  effectiveDate: string
  terminationDate: string
  contactName: string
  contactPhone: string
  claimsMailingAddress: string
  billingState: string
  submissionMethod: string
  providerPortalUrl: string
  ediSubmitterId: string
  status: PayorStatus
  activePayor: boolean
  electronicRemittance: boolean
  realTimeEligibility: boolean
  autoPostEraPayments: boolean
  contractNotes: string
}

export interface PayorFeeScheduleMeta {
  scheduleYear: string
  scheduleLabel: string
}

export interface PayorConfigDraft {
  overview: PayorOverviewForm
  plans: Plan[]
  policyRules: PayorPolicyRulesForm
  feeScheduleMeta: PayorFeeScheduleMeta
  feeSchedules: PayorFeeScheduleRow[]
  cappedRentals: PayorCappedRentalsForm
  ownershipTransfer: PayorOwnershipTransferForm
  priorAuth: PayorPriorAuthForm
}

export const WIZARD_TAB_ORDER: PayorConfigTabId[] = [
  'overview',
  'plans',
  'fee-schedules',
  'capped-rentals',
  'ownership-transfer',
  'prior-auth',
]

export function tabIdFromWizardStep(step: number): PayorConfigTabId {
  const index = Math.max(0, Math.min(WIZARD_TAB_ORDER.length - 1, step - 1))
  return WIZARD_TAB_ORDER[index]
}

export function wizardStepFromTabId(tabId: PayorConfigTabId): number {
  const index = WIZARD_TAB_ORDER.indexOf(tabId)
  return index >= 0 ? index + 1 : 1
}

export function normalizeNextStepTabId(nextStep: unknown): PayorConfigTabId | null {
  if (typeof nextStep !== 'string' || !nextStep.trim()) return null
  const slug = nextStep.trim().toLowerCase()
  const match = WIZARD_TAB_ORDER.find(
    (tab) => tab === slug || tab.replace(/-/g, '') === slug.replace(/-/g, '')
  )
  if (match) return match
  if (slug === 'plans-policies' || slug.includes('plan')) return 'plans'
  if (slug.includes('fee')) return 'fee-schedules'
  if (slug.includes('rental') || slug.includes('cap')) return 'capped-rentals'
  if (slug.includes('ownership') || slug.includes('transfer')) return 'ownership-transfer'
  if (slug.includes('prior') || slug.includes('auth')) return 'prior-auth'
  if (slug.includes('overview') || slug.includes('detail')) return 'overview'
  return null
}

export interface PayorOverviewErrors {
  payorName?: string
  contractType?: string
  effectiveDate?: string
}

export const fieldClass =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500'

export const labelClass = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5'
